import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
} from '@progress/kendo-react-charts';
import { produce } from 'immer';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { formatPrice } from '../../../../../../common/global/helpers';
import Loader from '../../../../../../common/widgets/Loader';
import { config } from '../../../../config';
import { CategoryStatisticModel } from '../../../../models/ClientOrders';
import { OrdersStatisticsModel } from '../../../../models/Orders';
import { StatisticsFilterModel } from '../../../../models/StatisticsFilterModel';
import { resources } from '../../../../resources/resources';
import { clientOrdersService } from '../../../../services/clientOrders';
import { EmptyStateAdminDashboardPopularBenefits } from '../../../Common/EmptyStates';

interface ClientOrdersStatisticsState {
  isLoading: boolean;
  model?: OrdersStatisticsModel;
}

interface ClientOrdersStatisticsProps {
  filters: StatisticsFilterModel;
}

export const ClientOrdersStatistics = (
  props: Readonly<ClientOrdersStatisticsProps>,
) => {
  const { t: translate } = useTranslation();
  const [state, setState] = useState<ClientOrdersStatisticsState>({
    isLoading: true,
  });

  useEffect(() => {
    const controller = new AbortController();

    const loadOrdersStatistics = async () => {
      const response = await clientOrdersService.getOrdersStatistics(
        props.filters,
        {
          signal: controller?.signal,
        },
      );

      if (controller.signal.aborted) return;

      if (!response.hasErrors) {
        setState(
          produce(draft => {
            draft.model = {
              totalAmount: response.data.totalAmount,
              statistics: response.data.statistics,
            };
          }),
        );
      }

      setState(
        produce(draft => {
          draft.isLoading = false;
        }),
      );
    };

    loadOrdersStatistics();

    return () => {
      controller.abort();
    };
  }, [props.filters]);

  const dataMapper = (statistics: CategoryStatisticModel[]) => {
    const colors = [
      '#83D0D5',
      '#3AB29C',
      '#FCB655',
      '#FB6F43',
      '#FF220D',
      '#B31C49',
      '#FF417A',
      '#FC81AD',
      '#E295F7',
      '#B816D8',
      '#751886',
      '#7727FA',
      '#318BFB',
      '#71F4E5',
      '#81C105',
    ];

    return statistics.map((category, index) => ({
      kind: category.categoryName,
      share: category.amount,
      color: index < colors.length ? colors[index] : '',
    }));
  };

  return (
    <div className="panel padding-xl admin-statistics-section">
      <h2 className="statistics-subtitle">
        {translate(resources.admin.statistics.popularBenefits)}
      </h2>

      <Loader loading={state.isLoading} loader={<Skeleton />}>
        {state.model && state.model?.totalAmount > 0 ? (
          <div className="admin-donut">
            <Chart
              donutCenterRender={() =>
                DonutCenterRenderer(state?.model?.totalAmount)
              }
            >
              <ChartSeries>
                <ChartSeriesItem
                  holeSize={90}
                  type="donut"
                  data={dataMapper(state.model?.statistics)}
                  categoryField="kind"
                  field="share"
                  color={'red'}
                ></ChartSeriesItem>
              </ChartSeries>
              <ChartLegend visible={false} />
            </Chart>

            <Legend
              statistics={dataMapper(state.model.statistics)}
              totalAmount={state.model.totalAmount}
            />
          </div>
        ) : (
          <EmptyStateAdminDashboardPopularBenefits />
        )}
      </Loader>
    </div>
  );
};

const Legend = (props: {
  statistics: { kind: string; share: number; color: string }[];
  totalAmount: number;
}) => {
  if (_.isNil(props.statistics)) {
    return null;
  }

  return (
    <ul className="admin-donut-legend">
      {props.statistics.map((item: any, index: number) => {
        return (
          <li key={index}>
            <span>
              <span>
                <span
                  className="legend-bullet"
                  style={{ borderColor: item.color }}
                ></span>
                {_.unescape(item.kind)}
              </span>
              <span className="legend-value">
                <span className="legent-amount">
                  {formatPrice(item.share)} {config.currency}
                </span>
                <span>
                  {Math.round((item.share / props.totalAmount) * 100)}%
                </span>
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const DonutCenterRenderer = (totalAmount?: number) => {
  const { t: translate } = useTranslation();

  return (
    <span className="donut-center">
      <span>{translate(resources.statistics.spentAmount)}</span>
      <div>
        {formatPrice(totalAmount)} <sup>{config.currency}</sup>
      </div>
    </span>
  );
};
